import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Container,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";

// Styled components
// Increase the size of the SliderContainer
const SliderContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  maxWidth: "1200px", // Increased from 1400px
  margin: "20px 0", // Increased from 10px
  overflow: "hidden",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexDirection: "column",
  backgroundColor: "white",
  borderRadius: "12px",
  padding: "30px 0", // Increased from 20px
  boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
}));

const SlideIndicator = styled(Box)(({ theme, active }) => ({
  width: active ? "14px" : "10px",
  height: active ? "14px" : "10px",
  borderRadius: "50%",
  backgroundColor: active ? "#30b6c9" : "#ccc",
  margin: "0 2px",
  cursor: "pointer",
  transition: "all 0.3s ease",
}));

const CardImage = styled("img")(({ active }) => ({
  width: "100%",
  height: "auto",
  display: "block",
  objectFit: "contain",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
  transition: "all 0.4s ease",
  transform: active ? "scale(1)" : "scale(0.9)",
}));

const RegisterButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#30b6c9",
  color: "white",
  borderRadius: "25px",
  padding: "12px 40px",
  fontSize: "18px",
  fontWeight: "bold",
  "&:hover": {
    backgroundColor: "#2599a9",
  },
}));

const NavButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: "rgba(48, 182, 201, 0.8)",
  color: "#fff",
  "&:hover": {
    backgroundColor: "#2599a9",
  },
  width: "40px",
  height: "40px",
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 20,
}));

const CardSlider = ({ cards, title, nav, regnav }) => {
  const getEmbedUrl = (url) => {
    const match = url.match(
      /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([\w-]{11})/
    );
    return match ? match[1] : "";
  };

  const [activeIndex, setActiveIndex] = useState(0);
  const theme = useTheme();
  const navigate = useNavigate();
  // Media queries for different screen sizes
  const isDesktop = useMediaQuery(theme.breakpoints.up("md")); // >= 900px
  const isMediumMobile = useMediaQuery(
    "(min-width:500px) and (max-width:767px)"
  );
  const isSmallMobile = useMediaQuery("(max-width:499px)");
  const [showMessage, setShowMessage] = useState(false);

  const handleClick = () => {
    setShowMessage(true);

    // Optional: Hide the message after a few seconds
    setTimeout(() => {
      setShowMessage(false);
    }, 3000);
  };

  // Determine height based on screen size
  const getContainerHeight = () => {
    if (isDesktop) return "750px";
    if (isMediumMobile) return "600px";
    if (isSmallMobile) return "320px";
    return "700px"; // Default fallback
  };

  const goToSlide = (index) => {
    setActiveIndex(index);
  };

  const nextSlide = () => {
    const newIndex = activeIndex === cards.length - 1 ? 0 : activeIndex + 1;
    setActiveIndex(newIndex);
  };

  const prevSlide = () => {
    const newIndex = activeIndex === 0 ? cards.length - 1 : activeIndex - 1;
    setActiveIndex(newIndex);
  };

  const handleTravel = () => {
    if (regnav) {
      // navigate("/admission");
      handleClick();
    } else if (nav) {
      navigate("/dashboard/aptitude-test");
    }
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        py: 4,
        backgroundColor: "white",
        my: 6,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignContent: "center",
      }}
    >
      <Typography
        variant="h3"
        component="h1"
        gutterBottom
        align="center"
        sx={{
          fontWeight: "bold",
          mb: 1,
          fontSize: {
            xs: "1.8rem",
            sm: "2.2rem",
            md: "2.5rem",
          },
          fontFamily: "Aclonica",
        }}
      >
        {title}
      </Typography>

      <SliderContainer>
        {/* Card Container */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: getContainerHeight(), // Dynamic height based on screen size
            backgroundColor: "white",
          }}
        >
          {/* Navigation Buttons */}
          {cards.length > 1 && (
            <>
              <NavButton
                onClick={prevSlide}
                sx={{ left: { xs: "5px", md: "-50px" } }}
              >
                &#10094;
              </NavButton>

              <NavButton
                onClick={nextSlide}
                sx={{ right: { xs: "5px", md: "-50px" } }}
              >
                &#10095;
              </NavButton>
            </>
          )}

          {/* Mobile: Single card slider, Desktop: Stacked special slider */}
          {!isDesktop ? (
            // Mobile view: Single card visible at a time
            <Box
              sx={{
                position: "relative",

                width: "100%",
              }}
            >
              {cards.map((card, index) => (
                <Box
                  key={index}
                  sx={{
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    display: index === activeIndex ? "block" : "none",
                    opacity: index === activeIndex ? 1 : 0,
                    transition: "opacity 0.5s ease",
                    visibility: index === activeIndex ? "visible" : "hidden",
                    padding: "0 20px",
                    backgroundColor: "white",
                  }}
                >
                  <CardImage src={`${card.url}`} alt={`Slide ${index + 1}`} />
                  {card.youtubeLink && (
                    <Box
                      sx={{
                        mt: 2,
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <iframe
                        width="100%"
                        height={
                          isSmallMobile ? "180" : isDesktop ? "300" : "250"
                        }
                        src={`https://www.youtube.com/embed/${getEmbedUrl(
                          card.youtubeLink
                        )}`}
                        title="Course Preview Video"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        style={{ borderRadius: "12px", maxWidth: "640px" }}
                      ></iframe>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            // Desktop view: Stacked cards with spacing
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                overflow: "visible",
              }}
            >
              {cards.map((card, index) => {
                const isActive = index === activeIndex;

                return (
                  <Box
                    key={index}
                    sx={{
                      position: "absolute",
                      left: index === 0 ? "6%" : "45%",
                      width: "50%",
                      height: "100%",
                      cursor: "pointer",
                      transition: "all 0.4s ease",
                      transform: `translateX(${
                        index === activeIndex
                          ? "0"
                          : index < activeIndex
                          ? "-20%"
                          : "20%"
                      })`,
                      opacity: isActive ? 1 : 0.5,
                      filter: isActive ? "blur(0)" : "blur(2px)",
                      zIndex: isActive ? 10 : 5,
                      transform: isActive ? "scale(1.2)" : "scale(0.6)",
                      display: "flex",
                      flexDirection: "column", // stack items vertically
                      alignItems: "center",
                      justifyContent: "center",
                      padding: isActive ? "0" : "5%", // Reduce padding when active
                    }}
                    onClick={() => goToSlide(index)}
                  >
                    <CardImage src={`${card.url}`} alt={`Slide ${index + 1}`} />
                    {card.youtubeLink && (
                      <Box
                        sx={{
                          mt: 2,
                          width: "100%",
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <iframe
                          width="100%"
                          height={
                            isSmallMobile ? "180" : isDesktop ? "300" : "250"
                          }
                          src={`https://www.youtube.com/embed/${getEmbedUrl(
                            card.youtubeLink
                          )}`}
                          title="Course Preview Video"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          style={{ borderRadius: "12px", maxWidth: "640px" }}
                        ></iframe>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {/* Slider Navigation Dots */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mt: 4,
            mb: 1,
            backgroundColor: "white",
          }}
        >
          {cards.map((_, index) => (
            <SlideIndicator
              key={index}
              active={index === activeIndex}
              onClick={() => goToSlide(index)}
            />
          ))}
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignContent: "center",
            gap: 2,
            mt: 4,
          }}
        >
          <RegisterButton
            variant="contained"
            size="large"
            sx={{
              background: "linear-gradient(to right, #4ECDC4, #2196F3)",
              "&:hover": { backgroundColor: "#2798ac" },
              fontSize: { xs: "1rem", md: "1.125rem" },
              py: { xs: 1.5, md: 2 },
              fontWeight: "400",
            }}
            onClick={() => {
              handleTravel();
            }}
          >
            REGISTER NOW!
          </RegisterButton>
          {showMessage && (
            <Typography
              sx={{
                color: "red",
                mt: 1,
                fontSize: "14px",
                textAlign: "center",
                display: "flex",
                alignItems: "center",
              }}
            >
              Registrations have not started yet!
            </Typography>
          )}
        </Box>
      </SliderContainer>
    </Container>
  );
};

export default CardSlider;
